import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { listFlashSales } from '@/lib/api/core';

/**
 * useFlashSales — promos flash activas vía `GET /v1/flash-sales` (api-core,
 * `active_only=true`).
 *
 * Unidades: `original_price` y `final_price` están en COP enteros
 * (no centavos). `discount_percentage` es porcentaje 0-100.
 */

export type FlashSale = {
  id: string;
  partnerId: string | null;
  title: string;
  description: string | null;
  type: string;
  originalPrice: number;
  finalPrice: number;
  discountPercentage: number;
  quantityAvailable: number;
  quantitySold: number;
  maxPerUser: number | null;
  startDate: string | null;
  endDate: string | null;
  membershipRequired: string | null;
  imageUrl: string | null;
  termsConditions: string | null;
};

function toNumber(v: unknown, fallback = 0): number {
  const n = typeof v === 'string' ? Number.parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

async function fetchFlashSales(): Promise<FlashSale[]> {
  const data = await listFlashSales();

  return data.map((r) => {
    const main =
      typeof (r as Record<string, unknown>).main_image === 'string'
        ? String((r as Record<string, unknown>).main_image).trim()
        : '';
    const alt = typeof r.image_url === 'string' ? r.image_url.trim() : '';
    return {
      id: String(r.id),
      partnerId: (r.partner_id as string | null) ?? null,
      title: typeof r.title === 'string' && r.title.trim() ? r.title : 'Promo flash',
      description: (r.description as string | null) ?? null,
      type: typeof r.type === 'string' ? r.type : 'event',
      originalPrice: toNumber(r.original_price),
      finalPrice: toNumber(r.final_price),
      discountPercentage: toNumber(r.discount_percentage),
      quantityAvailable: toNumber(r.quantity_available),
      quantitySold: toNumber(r.quantity_sold),
      maxPerUser:
        typeof r.max_per_user === 'number' || typeof r.max_per_user === 'string'
          ? toNumber(r.max_per_user, 0) || null
          : null,
      startDate: (r.start_date as string | null) ?? null,
      endDate: (r.end_date as string | null) ?? null,
      membershipRequired: (r.membership_required as string | null) ?? null,
      imageUrl: main || alt || null,
      termsConditions: (r.terms_conditions as string | null) ?? null,
    };
  });
}

export function useFlashSales(): UseQueryResult<FlashSale[]> {
  return useQuery({
    queryKey: ['flash-sales', 'active'],
    queryFn: fetchFlashSales,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
